import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fallback: garantir que loading seja false após timeout máximo
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.warn('AuthContext: Timeout de segurança - desabilitando loading');
        setLoading(false);
      }
    }, 3000); // 3 segundos máximo

    return () => clearTimeout(safetyTimeout);
  }, [loading]);

  // Verificar se há usuário logado ao carregar
  useEffect(() => {
    const checkAuth = async () => {
      // Timeout máximo de 2 segundos para não bloquear renderização
      const maxWaitTime = setTimeout(() => {
        setLoading(false);
      }, 2000);

      try {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
          try {
            // Verificar se token ainda é válido com timeout curto
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Timeout')), 1500)
            );
            
            const userData = await Promise.race([
              authAPI.me(),
              timeoutPromise
            ]);
            
            clearTimeout(maxWaitTime);
            setUser(userData);
            setLoading(false);
          } catch (error) {
            console.error('Erro ao verificar autenticação:', error);
            // Não bloquear renderização se houver erro
            // Apenas limpar dados inválidos silenciosamente
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            clearTimeout(maxWaitTime);
            setLoading(false);
          }
        } else {
          clearTimeout(maxWaitTime);
          setLoading(false);
        }
      } catch (error) {
        console.error('Erro crítico ao verificar autenticação:', error);
        clearTimeout(maxWaitTime);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      
      // Se precisa verificação de código (2FA)
      if (response.requiresVerification) {
        return {
          success: false,
          requiresVerification: true,
          verificationType: 'login',
          email: response.email || email,
          message: response.message || 'Código de verificação enviado para seu email',
          developmentCode: response.developmentCode // Código em modo desenvolvimento
        };
      }
      
      // Se a resposta contém token, é sucesso (mesmo se tiver status de bloqueio)
      if (response.token) {
        // Salvar token e usuário
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        setUser(response.user);
        
        return { 
          success: true,
          mustPay: response.mustPay || false,
          redirectTo: response.redirectTo,
          subscription: response.subscription,
          blocked: response.blocked,
          code: response.code,
          message: response.message
        };
      }
      
      // Se não tem token mas tem mustPay, ainda tratar como sucesso (para redirecionar)
      if (response.mustPay || response.redirectTo) {
        return {
          success: true,
          mustPay: true,
          redirectTo: response.redirectTo || '/billing',
          subscription: response.subscription,
          blocked: true,
          code: response.code,
          message: response.message
        };
      }
      
      return { 
        success: true,
        mustPay: response.mustPay || false,
        redirectTo: response.redirectTo,
        subscription: response.subscription
      };
    } catch (error) {
      const errorData = error.response?.data;
      // EMAIL_NOT_VERIFIED é um fluxo esperado (não é "erro" de sistema)
      if (errorData?.requiresVerification || errorData?.code === 'EMAIL_NOT_VERIFIED') {
        console.log('ℹ️ Login bloqueado: email não verificado. Iniciando fluxo de verificação.');
      } else {
        console.error('Erro no login:', error);
      }
      
      // Se precisa verificação de email
      if (errorData?.requiresVerification || errorData?.code === 'EMAIL_NOT_VERIFIED') {
        return {
          success: false,
          requiresVerification: true,
          verificationType: 'email',
          email: email,
          error: errorData?.error || 'Email não verificado',
          code: errorData?.code
        };
      }
      
      // Se o erro tem mustPay ou redirectTo, ainda permitir login e redirecionar
      if (errorData?.mustPay || errorData?.redirectTo || errorData?.token) {
        // Se tiver token no erro, salvar e permitir acesso
        if (errorData?.token) {
          localStorage.setItem('token', errorData.token);
          localStorage.setItem('user', JSON.stringify(errorData.user || {}));
          setUser(errorData.user);
          
          return {
            success: true,
            mustPay: errorData.mustPay || false,
            redirectTo: errorData.redirectTo || '/billing',
            subscription: errorData.subscription,
            blocked: errorData.blocked,
            code: errorData.code,
            message: errorData.message
          };
        }
        
        // Mesmo sem token, se tem redirectTo, tratar como sucesso para redirecionar
        return {
          success: true,
          mustPay: true,
          redirectTo: errorData.redirectTo || '/billing',
          subscription: errorData.subscription,
          blocked: true,
          code: errorData.code,
          message: errorData.message
        };
      }
      
      return {
        success: false,
        error: errorData?.error || errorData?.message || 'Erro ao fazer login',
        mustPay: errorData?.mustPay || false,
        redirectTo: errorData?.redirectTo,
        code: errorData?.code,
        subscription: errorData?.subscription
      };
    }
  };

  const verifyLogin = async (email, code) => {
    try {
      const response = await authAPI.verifyLogin(email, code);
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        
        return {
          success: true,
          mustPay: response.mustPay || false,
          redirectTo: response.redirectTo,
          subscription: response.subscription,
          blocked: response.blocked,
          code: response.code,
          message: response.message
        };
      }
      
      return {
        success: false,
        error: response.error || 'Erro ao verificar código'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao verificar código'
      };
    }
  };

  const register = async (email, password, name, phone, cpf) => {
    try {
      const response = await authAPI.register(email, password, name, phone, cpf);
      
      // Se precisa verificar email
      if (response.requiresVerification) {
        return {
          success: false,
          requiresVerification: true,
          email: response.email || email,
          message: response.message || 'Código de verificação enviado para seu email',
          developmentCode: response.developmentCode // Código em modo desenvolvimento
        };
      }
      
      // Se já tem token (caso não precise verificação)
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }
      
      return { 
        success: true,
        requiresVerification: response.requiresVerification || false
      };
    } catch (error) {
      console.error('Erro no registro:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao criar conta'
      };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    navigate('/login');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyLogin, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export default AuthContext;



